// frontend\src\hooks\useAnalytics.ts
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  AnalyticsResponse,
  AnalyticsDataPoint,
  AnalyticsCompareMode,
  AnalyticsCompareSeries,
  PopularProduct,
  TopCustomer,
} from '../types';

const useAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsResponse>({
    data: [],
    geoData: [],
    customerTypeData: [],
    tagData: [],
    last_updated: '',
    totalMetric: 0,
    percentageChange: null,
  });
  const [popularProducts, setPopularProducts] = useState<PopularProduct[] | null>(null);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [compareMode, setCompareMode] = useState<AnalyticsCompareMode>('previousPeriod');
  const [comparePeriods, setComparePeriods] = useState<number>(1);
  const [timeBase, setTimeBase] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>('daily');
  const [metric, setMetric] = useState<'sales' | 'orderCount' | 'itemCount'>('sales');
  const [search, setSearch] = useState<string>('');
  const [compareSearch, setCompareSearch] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedCustomerType, setSelectedCustomerType] = useState<string | null>(null);
  const [selectedCompareCustomerType, setSelectedCompareCustomerType] = useState<string | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedCompareTag, setSelectedCompareTag] = useState<string | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [selectedCompareSupplier, setSelectedCompareSupplier] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const params: any = {
        start_date: startDate,
        end_date: endDate,
        time_base: timeBase,
        metric,
        search: search || undefined,
        region: selectedRegion || undefined,
        country: selectedCountry || undefined,
        customer_type: selectedCustomerType || undefined,
        customer_name: selectedCustomerName || undefined,
        tag: selectedTag || undefined,
        supplier: selectedSupplier || undefined,
        compare_mode: compareSearch ? undefined : compareMode,
        compare_periods: compareSearch ? undefined : comparePeriods,
      };
      const primaryResponse = await api.get('/analytics', { params });
      let compareData: AnalyticsDataPoint[] = [];
      let compareSeries: AnalyticsCompareSeries[] = [];
      let compareFilterLabel: string | undefined;
      if (compareSearch) {
        const compareParams = {
          ...params,
          search: compareSearch || undefined,
          compare_tag: selectedCompareTag || undefined,
          compare_supplier: selectedCompareSupplier || undefined,
          tag: undefined,
          supplier: undefined,
          compare_mode: undefined,
          customer_type: selectedCompareCustomerType || undefined,
        };
        const compareResponse = await api.get('/analytics-compare', { params: compareParams });
        compareData = compareResponse.data?.data || [];
        compareFilterLabel = compareResponse.data.compareFilterLabel;
        // The comparison-filter flow is always a single series over the same date range.
        compareSeries = compareData.length
          ? [{ startDate, endDate, offset: 1, data: compareData, label: compareFilterLabel }]
          : [];
      } else {
        compareSeries = primaryResponse.data?.compareSeries || [];
        compareData = primaryResponse.data?.compareData || [];
      }
      let percentageChange = primaryResponse.data?.percentageChange;
      if (compareSearch && compareData.length > 0) {
        const totalMetric = primaryResponse.data?.totalMetric || 0;
        const compareTotal = compareData.reduce((sum, row) => sum + (row[metric] || 0), 0);
        if (compareTotal > 0) {
          percentageChange = ((totalMetric - compareTotal) / compareTotal) * 100;
        }
      }
      setAnalyticsData({
        ...primaryResponse.data,
        compareData,
        compareSeries,
        compareFilterLabel,
        percentageChange,
        geoData: primaryResponse.data?.geoData || [],
        customerTypeData: primaryResponse.data?.customerTypeData || [],
        tagData: primaryResponse.data?.tagData || [],
      });
    } catch (error: any) {
      console.error('Analytics fetch error:', error.response?.data || error.message);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        setError(error.response?.data?.error || 'Failed to fetch analytics data');
      }
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    timeBase,
    metric,
    search,
    compareSearch,
    selectedRegion,
    selectedCountry,
    selectedCustomerType,
    selectedCompareCustomerType,
    selectedCustomerName,
    selectedTag,
    selectedCompareTag,
    selectedSupplier,
    selectedCompareSupplier,
    compareMode,
    comparePeriods,
    navigate,
  ]);

  const fetchPopularProducts = async () => {
    if (!startDate || !endDate) return;
    setLoadingProducts(true);
    try {
      const params: any = {
        start_date: startDate,
        end_date: endDate,
        search: search || undefined,
        region: selectedRegion || undefined,
        country: selectedCountry || undefined,
        customer_type: selectedCustomerType || undefined,
        customer_name: selectedCustomerName || undefined,
        tag: selectedTag || undefined,
        supplier: selectedSupplier || undefined,
        compare_mode: compareSearch ? undefined : compareMode,
      };
      const primaryResponse = await api.get('/report-products', { params });
      let compareResponseData: { sku: string; num_sold: number }[] = [];
      if (compareSearch) {
        const compareParams = {
          start_date: startDate,
          end_date: endDate,
          search: compareSearch || undefined,
          compare_tag: selectedCompareTag || undefined,
          compare_supplier: selectedCompareSupplier || undefined,
          region: selectedRegion || undefined,
          country: selectedCountry || undefined,
          customer_type: selectedCompareCustomerType || undefined,
          customer_name: selectedCustomerName || undefined,
        };
        const compareResponse = await api.get('/report-products-compare', { params: compareParams });
        compareResponseData = compareResponse.data?.data || [];
      } else {
        compareResponseData = primaryResponse.data?.compareData || [];
      }
      const compareMap = new Map(compareResponseData.map((item) => [item.sku, item.num_sold]));
      const products: PopularProduct[] = (primaryResponse.data?.data || []).map((item: any) => ({
        product_name: item.product_name,
        sku: item.sku,
        revenue: parseFloat(item.revenue) || 0,
        num_sold: parseInt(item.num_sold, 10) || 0,
        num_sold_compare: compareMap.get(item.sku) || 0,
        percent_change:
          compareMap.has(item.sku) && (compareMap.get(item.sku) || 0) > 0
            ? ((parseInt(item.num_sold, 10) - (compareMap.get(item.sku) || 0)) / (compareMap.get(item.sku) || 0)) * 100
            : null,
      }));
      setPopularProducts(products);
      setTopCustomers(null); // Reset other report
    } catch (error: any) {
      console.error('Popular products fetch error:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to fetch popular products data');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchTopCustomers = async () => {
    if (!startDate || !endDate) return;
    setLoadingCustomers(true);
    try {
      const params: any = {
        start_date: startDate,
        end_date: endDate,
        search: search || undefined,
        region: selectedRegion || undefined,
        country: selectedCountry || undefined,
        customer_type: selectedCustomerType || undefined,
        customer_name: selectedCustomerName || undefined,
        tag: selectedTag || undefined,
        supplier: selectedSupplier || undefined,
        compare_mode: compareSearch ? undefined : compareMode,
      };
      const primaryResponse = await api.get('/report-customers', { params });
      let compareResponseData: { customer_name: string; num_sold: number }[] = [];
      if (compareSearch) {
        const compareParams = {
          start_date: startDate,
          end_date: endDate,
          search: compareSearch || undefined,
          compare_tag: selectedCompareTag || undefined,
          compare_supplier: selectedCompareSupplier || undefined,
          region: selectedRegion || undefined,
          country: selectedCountry || undefined,
          customer_type: selectedCompareCustomerType || undefined,
          customer_name: selectedCustomerName || undefined,
        };
        const compareResponse = await api.get('/report-customers-compare', { params: compareParams });
        compareResponseData = compareResponse.data?.data || [];
      } else {
        compareResponseData = primaryResponse.data?.compareData || [];
      }
      const compareMap = new Map(compareResponseData.map((item) => [item.customer_name, item.num_sold]));
      const customers: TopCustomer[] = (primaryResponse.data?.data || []).map((item: any) => ({
        customer_name: item.customer_name,
        revenue: parseFloat(item.revenue) || 0,
        num_sold: parseInt(item.num_sold, 10) || 0,
        num_sold_compare: compareMap.get(item.customer_name) || 0,
        percent_change:
          compareMap.has(item.customer_name) && (compareMap.get(item.customer_name) || 0) > 0
            ? ((parseInt(item.num_sold, 10) - (compareMap.get(item.customer_name) || 0)) / (compareMap.get(item.customer_name) || 0)) * 100
            : null,
      }));
      setTopCustomers(customers);
      setPopularProducts(null); // Reset other report
    } catch (error: any) {
      console.error('Top customers fetch error:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to fetch top customers data');
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    setAnalyticsData((prev) => ({ ...prev, geoData: [], customerTypeData: [], tagData: [] }));
    setPopularProducts(null);
    setTopCustomers(null); // Reset both reports when filters change
    fetchAnalytics();
  }, [
    fetchAnalytics,
  ]);

  return {
    analyticsData,
    popularProducts,
    topCustomers,
    loading,
    loadingProducts,
    loadingCustomers,
    error,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    compareMode,
    setCompareMode,
    comparePeriods,
    setComparePeriods,
    timeBase,
    setTimeBase,
    metric,
    setMetric,
    search,
    setSearch,
    compareSearch,
    setCompareSearch,
    selectedRegion,
    setSelectedRegion,
    selectedCountry,
    setSelectedCountry,
    selectedCustomerType,
    setSelectedCustomerType,
    selectedCompareCustomerType,
    setSelectedCompareCustomerType,
    selectedCustomerName,
    setSelectedCustomerName,
    selectedTag,
    setSelectedTag,
    selectedCompareTag,
    setSelectedCompareTag,
    selectedSupplier,
    setSelectedSupplier,
    selectedCompareSupplier,
    setSelectedCompareSupplier,
    fetchAnalytics,
    fetchPopularProducts,
    fetchTopCustomers,
    setPopularProducts,
    setTopCustomers,
  };
};

export default useAnalytics;